import type { SortSpec } from '@moment/horizon-data-access/platform'
import type { QuoteSideKey } from './security-types'

import { quoteYieldTaxColumn } from './security-row-adapter'
import {
  bookFields,
  evalFields,
  SECURITY_ENTITY,
  SECURITY_ID_FIELD,
} from './security-wire-fields'
import { securityFieldForInstrumentField } from './translate-filter-object'
import { OBJECTIVE_SCORE_ALIAS } from './translate-objective'

/** A live-inventory sort key, e.g. `maturity_date` or `asks_ytw`. */
export interface SecuritySortKey {
  field: string
  direction: 'asc' | 'desc'
}

export interface SecuritySortOptions {
  side: QuoteSideKey
  useEvalsAsOrders: boolean
  /** Whether the request selects the objective score computed column. */
  hasObjective: boolean
  isFieldAvailable: (field: string) => boolean
  /** Whether the request projects a tax computed column, so a sort can name its alias. */
  isTaxColumnProjected: (column: string) => boolean
}

/** The tax computed columns a sort reads; the request must project them. */
export function sortTaxColumns(
  keys: ReadonlyArray<SecuritySortKey>,
  useEvalsAsOrders: boolean,
): string[] {
  return keys
    .map(({ field }) => quoteYieldTaxColumn(field, useEvalsAsOrders))
    .filter((column) => column !== undefined)
}

export interface SecuritySortPlan {
  sort: SortSpec[]
  /** Mapped keys the schema does not serve yet; they are left out of `sort`. */
  unsupportedFields: string[]
  /** Keys with no Security mapping at all; they are left out of `sort`. */
  unmappedFields: string[]
}

// Live inventory's `{side}s_*` sort keys rank by the top of book; evals-as-orders
// orgs quote the side's MPE instead.
function topOfBookSortField(
  field: string,
  side: QuoteSideKey,
  useEvalsAsOrders: boolean,
): string | undefined {
  const quote = useEvalsAsOrders ? evalFields(side) : bookFields(side)
  switch (field) {
    case `${side}s_price`:
      return quote.price
    case `${side}s_ytw`:
      return quote.ytw
    case `${side}s_ytm`:
      return quote.ytm
    default:
      return undefined
  }
}

/**
 * Live-inventory sort keys as Security sort specs, ending in `security_id` so
 * offset pages stay stable across requests.
 */
export function translateSort(
  keys: ReadonlyArray<SecuritySortKey>,
  options: SecuritySortOptions,
): SecuritySortPlan {
  const sort: SortSpec[] = []
  const unsupportedFields: string[] = []
  const unmappedFields: string[] = []
  for (const { field, direction } of keys) {
    if (field === OBJECTIVE_SCORE_ALIAS) {
      if (options.hasObjective) sort.push({ field, direction })
      else unsupportedFields.push(field)
      continue
    }
    const taxColumn = quoteYieldTaxColumn(field, options.useEvalsAsOrders)
    if (taxColumn) {
      if (options.isTaxColumnProjected(taxColumn)) {
        sort.push({ field: taxColumn, direction })
      } else unsupportedFields.push(field)
      continue
    }
    const securityField =
      topOfBookSortField(field, options.side, options.useEvalsAsOrders) ??
      securityFieldForInstrumentField(field, options.side)
    if (!securityField) {
      unmappedFields.push(field)
      continue
    }
    if (!options.isFieldAvailable(securityField)) {
      unsupportedFields.push(field)
      continue
    }
    sort.push({ entity: SECURITY_ENTITY, field: securityField, direction })
  }
  sort.push({
    entity: SECURITY_ENTITY,
    field: SECURITY_ID_FIELD,
    direction: 'asc',
  })
  return { sort, unsupportedFields, unmappedFields }
}
